import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import AdminSidebar from '../../components/admin/AdminSidebar';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent } from '../../components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from '../../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Plus, ArrowLeft, Trash2, ClipboardList, CheckCircle2, Circle, Save, TrendingUp } from 'lucide-react';

const STAGE_COLORS = {
  'Não iniciado': 'bg-gray-100 text-gray-700',
  'Inexistente': 'bg-red-100 text-red-800',
  'Inicial': 'bg-orange-100 text-orange-800',
  'Em Desenvolvimento': 'bg-amber-100 text-amber-800',
  'Estabelecido': 'bg-teal-100 text-teal-800',
  'Avançado': 'bg-emerald-100 text-emerald-800',
};

function AssessmentsList() {
  const [companies, setCompanies] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [deleteId, setDeleteId] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const loadCompanies = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/companies/`, { withCredentials: true });
      setCompanies(data);
      if (data.length && !selectedCompany) setSelectedCompany(data[0].id);
    } catch { toast.error('Erro ao carregar empresas'); }
  }, [API_URL, selectedCompany]);

  const loadAssessments = useCallback(async () => {
    if (!selectedCompany) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/assessments/`, {
        withCredentials: true,
        params: { company_id: selectedCompany },
      });
      setAssessments(data);
    } catch { toast.error('Erro ao carregar avaliações'); }
    finally { setLoading(false); }
  }, [API_URL, selectedCompany]);

  useEffect(() => { loadCompanies(); }, [loadCompanies]);
  useEffect(() => { loadAssessments(); }, [loadAssessments]);

  const create = async () => {
    if (!selectedCompany) return;
    try {
      const { data } = await axios.post(
        `${API_URL}/api/assessments/`,
        { company_id: selectedCompany, title: newTitle || undefined },
        { withCredentials: true }
      );
      toast.success('Diagnóstico criado');
      setDialogOpen(false);
      setNewTitle('');
      navigate(`/admin/assessments/${data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar');
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/assessments/${id}`, { withCredentials: true });
      toast.success('Removida');
      loadAssessments();
    } catch { toast.error('Erro ao remover'); }
    finally { setDeleteId(null); }
  };

  return (
    <AdminSidebar title="Diagnósticos" subtitle="Avaliação de maturidade inclusiva em 12 dimensões (modelo UISA)">
      <Card className="mb-4">
        <CardContent className="p-4 flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[240px]">
            <Label htmlFor="company-select" className="text-xs">Empresa Cliente</Label>
            <select
              id="company-select"
              value={selectedCompany}
              onChange={(e) => setSelectedCompany(e.target.value)}
              className="w-full h-10 px-3 rounded-md border border-input bg-white text-sm mt-1"
              data-testid="assessment-company-select"
            >
              <option value="">Selecione...</option>
              {companies.map((c) => (
                <option key={c.id} value={c.id}>{c.trade_name || c.legal_name}</option>
              ))}
            </select>
          </div>
          {selectedCompany && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="new-assessment-button"><Plus className="mr-2 h-4 w-4" /> Novo Diagnóstico</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Novo Diagnóstico</DialogTitle>
                  <DialogDescription>Cria uma avaliação inicial com todas as 12 dimensões pontuadas em 0. Você pontua uma por uma.</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                  <Label htmlFor="title">Título (opcional)</Label>
                  <Input id="title" placeholder="Ex: Diagnóstico Inicial 2026" value={newTitle} onChange={(e) => setNewTitle(e.target.value)} data-testid="assessment-title-input" />
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
                    <Button onClick={create} data-testid="create-assessment-button">Criar</Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </CardContent>
      </Card>

      {!selectedCompany ? (
        <Card><CardContent className="p-12 text-center text-gray-500">Selecione uma empresa para ver seus diagnósticos.</CardContent></Card>
      ) : loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div></div>
      ) : assessments.length === 0 ? (
        <Card><CardContent className="p-12 text-center text-gray-500">
          <ClipboardList size={40} className="mx-auto text-gray-300 mb-3" />
          Nenhum diagnóstico. Clique em &quot;Novo Diagnóstico&quot; para iniciar.
        </CardContent></Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Diagnóstico</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Score</TableHead>
                  <TableHead>Estágio</TableHead>
                  <TableHead>Criado em</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {assessments.map((a) => (
                  <TableRow key={a.id} data-testid={`assessment-row-${a.id}`} className="cursor-pointer hover:bg-gray-50" onClick={() => navigate(`/admin/assessments/${a.id}`)}>
                    <TableCell><div className="font-medium">{a.title}</div>{a.notes && <div className="text-xs text-gray-500 line-clamp-1">{a.notes}</div>}</TableCell>
                    <TableCell>
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${a.status === 'completed' ? 'bg-emerald-100 text-emerald-800' : 'bg-blue-100 text-blue-800'}`}>
                        {a.status === 'completed' ? <><CheckCircle2 size={10} /> Concluído</> : <><Circle size={10} /> Rascunho</>}
                      </span>
                    </TableCell>
                    <TableCell className="font-mono font-bold">{a.overall_score.toFixed(2)}</TableCell>
                    <TableCell><span className={`inline-block px-2 py-1 rounded text-xs font-semibold ${STAGE_COLORS[a.maturity_stage] || 'bg-gray-100'}`}>{a.maturity_stage}</span></TableCell>
                    <TableCell className="text-xs text-gray-500">{new Date(a.created_at).toLocaleDateString('pt-BR')}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setDeleteId(a.id); }} data-testid={`delete-assessment-${a.id}`}><Trash2 className="h-4 w-4" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Excluir diagnóstico</AlertDialogTitle><AlertDialogDescription>Esta ação é permanente.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => remove(deleteId)}>Confirmar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminSidebar>
  );
}

function AssessmentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [assessment, setAssessment] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [asRes, catRes] = await Promise.all([
        axios.get(`${API_URL}/api/assessments/${id}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/assessments/dimensions/catalog`, { withCredentials: true }),
      ]);
      setAssessment(asRes.data);
      setCatalog(catRes.data);
    } catch { toast.error('Erro ao carregar'); }
    finally { setLoading(false); }
  }, [API_URL, id]);

  useEffect(() => { load(); }, [load]);

  const updateDimension = (key, patch) => {
    setAssessment((prev) => {
      if (!prev) return prev;
      const dims = { ...prev.dimensions, [key]: { ...prev.dimensions[key], ...patch } };
      const scores = Object.values(dims).map((d) => d.score || 0);
      const avg = scores.reduce((a, b) => a + b, 0) / scores.length;
      return { ...prev, dimensions: dims, overall_score: Math.round(avg * 100) / 100 };
    });
    setDirty(true);
  };

  const save = async (extras = {}) => {
    setSaving(true);
    try {
      const dims = Object.fromEntries(
        Object.entries(assessment.dimensions).map(([k, v]) => [k, { score: v.score, evidence_notes: v.evidence_notes, evidence_urls: v.evidence_urls || [] }])
      );
      const { data } = await axios.put(
        `${API_URL}/api/assessments/${id}`,
        { dimensions: dims, notes: assessment.notes, title: assessment.title, ...extras },
        { withCredentials: true }
      );
      setAssessment(data);
      setDirty(false);
      toast.success(extras.status === 'completed' ? 'Diagnóstico concluído' : 'Salvo');
    } catch (error) { toast.error(error.response?.data?.detail || 'Erro ao salvar'); }
    finally { setSaving(false); }
  };

  if (loading) return <AdminSidebar title="Diagnóstico" subtitle=""><div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div></div></AdminSidebar>;
  if (!assessment || !catalog) return null;

  const filledCount = Object.values(assessment.dimensions).filter((d) => d.score > 0).length;
  const progress = Math.round((filledCount / catalog.dimensions.length) * 100);

  return (
    <AdminSidebar title={assessment.title} subtitle={`Diagnóstico de maturidade · ${filledCount}/${catalog.dimensions.length} dimensões pontuadas`}>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Button variant="outline" onClick={() => navigate('/admin/assessments')} data-testid="back-to-assessments">
          <ArrowLeft className="mr-2 h-4 w-4" /> Voltar
        </Button>
        <div className="flex-1" />
        <Button onClick={() => save()} disabled={saving || !dirty} data-testid="save-assessment"><Save className="mr-2 h-4 w-4" /> Salvar</Button>
        {assessment.status !== 'completed' && (
          <Button onClick={() => save({ status: 'completed' })} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="complete-assessment">
            <CheckCircle2 className="mr-2 h-4 w-4" /> Concluir Diagnóstico
          </Button>
        )}
      </div>

      {/* Summary card */}
      <Card className="mb-6" data-testid="assessment-summary">
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <div className="text-xs uppercase text-gray-500 font-semibold">Score geral</div>
              <div className="text-4xl font-bold text-gray-900 mt-1">{assessment.overall_score.toFixed(2)}<span className="text-lg text-gray-400">/4.00</span></div>
            </div>
            <div>
              <div className="text-xs uppercase text-gray-500 font-semibold">Estágio de maturidade</div>
              <div className={`inline-flex mt-2 px-3 py-1.5 rounded-lg font-bold ${STAGE_COLORS[assessment.maturity_stage] || 'bg-gray-100'}`}>{assessment.maturity_stage}</div>
            </div>
            <div>
              <div className="text-xs uppercase text-gray-500 font-semibold">Progresso</div>
              <div className="flex items-center gap-2 mt-2">
                <div className="flex-1 h-3 rounded-full bg-gray-100 overflow-hidden">
                  <div className="h-full bg-teal-500" style={{ width: `${progress}%` }} />
                </div>
                <span className="text-sm font-semibold">{progress}%</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Dimensions */}
      <div className="space-y-4">
        {catalog.dimensions.map((d, idx) => {
          const val = assessment.dimensions[d.key] || { score: 0, evidence_notes: '', evidence_urls: [] };
          return (
            <Card key={d.key} data-testid={`dimension-${d.key}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-teal-600">DIMENSÃO {idx + 1}</span>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mt-0.5">{d.label}</h3>
                    <p className="text-sm text-gray-600 mt-1">{d.description}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-3xl font-bold text-gray-900">{val.score}</div>
                    <div className="text-[10px] uppercase tracking-wider text-gray-500">/ 4</div>
                  </div>
                </div>
                <div className="grid grid-cols-5 gap-2 mb-3">
                  {catalog.score_levels.map((lvl) => (
                    <button
                      key={lvl.value}
                      onClick={() => updateDimension(d.key, { score: lvl.value })}
                      data-testid={`score-${d.key}-${lvl.value}`}
                      className={`p-2 rounded-md border-2 text-center transition-colors ${
                        val.score === lvl.value
                          ? 'border-teal-600 bg-teal-50 text-teal-900'
                          : 'border-gray-200 bg-white text-gray-600 hover:border-teal-300'
                      }`}
                    >
                      <div className="text-lg font-bold">{lvl.value}</div>
                      <div className="text-[10px] font-semibold uppercase tracking-wider">{lvl.label}</div>
                    </button>
                  ))}
                </div>
                <div>
                  <Label htmlFor={`ev-${d.key}`} className="text-xs">Evidências / observações</Label>
                  <Textarea
                    id={`ev-${d.key}`}
                    rows={2}
                    value={val.evidence_notes || ''}
                    onChange={(e) => updateDimension(d.key, { evidence_notes: e.target.value })}
                    placeholder="Descreva as evidências que sustentam esta pontuação..."
                    data-testid={`evidence-${d.key}`}
                    className="mt-1 text-sm"
                  />
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="mt-6 flex justify-end">
        <Button onClick={() => save()} disabled={saving || !dirty} data-testid="save-assessment-bottom">
          <Save className="mr-2 h-4 w-4" /> Salvar Progresso
        </Button>
      </div>

      {dirty && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 bg-amber-500 text-white text-xs font-semibold px-3 py-1.5 rounded-full shadow-lg flex items-center gap-1 z-50">
          <TrendingUp size={12} /> Alterações não salvas
        </div>
      )}
    </AdminSidebar>
  );
}

export default function AssessmentsPage() {
  const { id } = useParams();
  return id ? <AssessmentDetail /> : <AssessmentsList />;
}
